import {
  Container,
  Grid,
  Typography,
  Box,
  Link,
  IconButton,
  Stack,
  Divider,
  Button,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  DirectionsCar as DirectionsCarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  ArrowForward as ArrowIcon,
  Business,
  LocalShipping,
  Verified,
  Copyright,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

const Footer = () => {
  const theme = useTheme();
  
  // Brand colors
  const brandColors = {
    green: '#a6ce39',
    black: '#050606',
    orange: '#ec7723',
    white: '#ffffff'
  };

  const quickLinks = [
    { text: "Home", href: "/" },
    { text: "About", href: "/about" },
    { text: "Our Fleet", href: "/trucks" },
    { text: "Booking", href: "/booking" },
    { text: "Contact", href: "/contact" },
  ];

  const services = [
    "Road Transport Services",
    "Cross-border Logistics",
    "Fleet Management",
    "Secure Transportation",
    "Custom Solutions",
  ];

  const socialLinks = [
    { icon: <Facebook />, href: "#", color: "#1877F2" },
    { icon: <Twitter />, href: "#", color: "#1DA1F2" },
    { icon: <LinkedIn />, href: "#", color: "#0077B5" },
    { icon: <Instagram />, href: "#", color: "#E4405F" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: brandColors.black,
        color: brandColors.white,
        pt: { xs: 8, md: 10 },
        pb: 4,
        mt: "auto",
        position: "relative",
        overflow: "hidden",
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.green} 100%)`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, ${alpha(brandColors.orange, 0.05)} 0%, transparent 70%)`,
          transform: 'translate(30%, 30%)',
        }
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: brandColors.white,
                  boxShadow: `0 6px 20px ${alpha(brandColors.orange, 0.3)}`,
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.1 }}>
                  TRANSCO
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight={800}
                  sx={{ 
                    color: brandColors.green,
                    letterSpacing: '2px',
                    fontSize: '1rem',
                  }}
                >
                  LOGISTICS
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" color={alpha(brandColors.white, 0.8)} paragraph sx={{ mb: 3, lineHeight: 1.8 }}>
              A sister company of HYDROPCE HYDRAULICS AND PNEUMATICS ENGINEERING.
              Leading transport company in The Kingdom of Eswatini providing added value 
              services in road transport from any destination.
            </Typography>
            
            <Chip
              label="EST. 2021"
              size="small"
              icon={<Verified sx={{ fontSize: 14 }} />}
              sx={{
                bgcolor: alpha(brandColors.green, 0.2),
                color: brandColors.green,
                fontWeight: 700,
                fontSize: "0.75rem",
                border: `1px solid ${alpha(brandColors.green, 0.3)}`,
                mb: 3,
              }}
            />
            
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: brandColors.white,
                    bgcolor: alpha(brandColors.white, 0.1),
                    borderRadius: '12px',
                    width: 40,
                    height: 40,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: social.color,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 6px 16px ${alpha(social.color, 0.4)}`,
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography 
              variant="h6" 
              gutterBottom 
              fontWeight={800}
              sx={{ 
                color: brandColors.white,
                mb: 3,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 40,
                  height: 3,
                  bgcolor: brandColors.orange,
                  borderRadius: '2px',
                }
              }}
            >
              Quick Links
            </Typography>
            <Stack spacing={2}>
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  component={RouterLink}
                  to={link.href}
                  color={alpha(brandColors.white, 0.8)}
                  underline="none"
                  sx={{
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    '&:hover': {
                      color: brandColors.orange,
                      transform: 'translateX(4px)',
                      '& .arrow-icon': {
                        opacity: 1,
                        transform: 'translateX(0)',
                      }
                    }
                  }}
                >
                  <ArrowIcon 
                    className="arrow-icon"
                    sx={{ 
                      fontSize: 14, 
                      opacity: 0,
                      transform: 'translateX(-8px)',
                      transition: 'all 0.3s ease',
                    }} 
                  />
                  {link.text}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography 
              variant="h6" 
              gutterBottom 
              fontWeight={800}
              sx={{ 
                color: brandColors.white,
                mb: 3,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 40,
                  height: 3,
                  bgcolor: brandColors.green,
                  borderRadius: '2px',
                }
              }}
            >
              Our Services
            </Typography>
            <Stack spacing={2}>
              {services.map((service, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: index % 2 === 0 ? brandColors.orange : brandColors.green,
                      flexShrink: 0,
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    color={alpha(brandColors.white, 0.8)}
                    sx={{ 
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: brandColors.white,
                      }
                    }}
                  >
                    {service}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={4}>
            <Typography 
              variant="h6" 
              gutterBottom 
              fontWeight={800}
              sx={{ 
                color: brandColors.white,
                mb: 3,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 40,
                  height: 3,
                  bgcolor: brandColors.orange,
                  borderRadius: '2px',
                }
              }}
            >
              Contact Info
            </Typography>
            
            <Stack spacing={3}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: alpha(brandColors.orange, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: brandColors.orange,
                    flexShrink: 0,
                  }}
                >
                  <LocationIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.6)} fontWeight={600} sx={{ mb: 0.5 }}>
                    Physical Address
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)}>
                    Nsulutane MR33 Road towards MK111 International Airport
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)} sx={{ mt: 0.5 }}>
                    P.O Box C2395, Hub Manzini, Eswatini
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: alpha(brandColors.green, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: brandColors.green,
                    flexShrink: 0,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.6)} fontWeight={600} sx={{ mb: 0.5 }}>
                    Phone Numbers
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)}>
                    +268 7602 1597
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)} sx={{ mt: 0.5 }}>
                    +268 2518 5080
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: alpha(brandColors.orange, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: brandColors.orange,
                    flexShrink: 0,
                  }}
                >
                  <EmailIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.6)} fontWeight={600} sx={{ mb: 0.5 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)}>
                    transcologistics26@gmail.com
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: alpha(brandColors.green, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: brandColors.green,
                    flexShrink: 0,
                  }}
                >
                  <TimeIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.6)} fontWeight={600} sx={{ mb: 0.5 }}>
                    Business Hours
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)}>
                    Mon - Fri: 8:00 - 17:00
                  </Typography>
                  <Typography variant="body2" color={alpha(brandColors.white, 0.8)} sx={{ mt: 0.5 }}>
                    Sat: 8:00 - 13:00
                  </Typography>
                </Box>
              </Box>
            </Stack>
            
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              endIcon={<ArrowIcon />}
              sx={{
                mt: 4,
                px: 4,
                py: 1.5,
                borderRadius: '14px',
                fontWeight: 700,
                background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                boxShadow: `0 6px 20px ${alpha(brandColors.orange, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
                  transform: 'translateY(-3px)',
                  boxShadow: `0 10px 25px ${alpha(brandColors.orange, 0.4)}`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              Get In Touch
            </Button>
          </Grid>
        </Grid>

        <Divider 
          sx={{ 
            my: { xs: 6, md: 8 }, 
            bgcolor: alpha(brandColors.white, 0.1),
          }} 
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Copyright sx={{ fontSize: 16, color: alpha(brandColors.white, 0.6) }} />
            <Typography variant="body2" color={alpha(brandColors.white, 0.6)}>
              {new Date().getFullYear()} TRANSCO LOGISTICS. All rights reserved.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ fontSize: 14, color: brandColors.orange }} />
            <Typography variant="body2" color={alpha(brandColors.white, 0.6)}>
              R7/61624 • VAT: 106 842 168
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping sx={{ fontSize: 14, color: brandColors.green }} />
            <Typography variant="body2" color={alpha(brandColors.white, 0.6)}>
              Sister company of HYDROPCE
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;